import {
  numVal,
  type Question,
  type QuestionnaireData,
  type QuestionnaireQuestionId,
  type RespondedQuestionnaireAnswers,
} from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { View } from 'react-native'
import { useFlowManager } from '~/components/flows/useFlowManager'
import { SectionContent, ViewTitle } from '~/components/layout/Sections'
import { WhatIsAikoaModal } from '~/components/questionnaires/WhatIsAikoa'
import RealtorCard from '~/components/realtors/RealtorCard'
import { Body } from '~/components/typography/Content'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { Text } from '~/components/ui/text'
import { ToggleButton } from '~/components/ui/toggle-button'

export function QuestionnairePageRenderer({
  page,
  answers,
  onUpdateAnswer,
  realtorId,
}: {
  page: QuestionnaireData['questions'][number]
  answers: RespondedQuestionnaireAnswers | null
  onUpdateAnswer: (questionId: QuestionnaireQuestionId, value: number | string) => void
  realtorId: string | null
}) {
  const { t } = useTranslations('feedback')
  const question = page
  const currentAnswer = answers?.find((a) => a.id === question.id)?.value

  const { previousPhase, nextPhase, currentPhaseIndex } = useFlowManager()
  const shouldShowTimingIntro = page.id === 'timing'

  return (
    <View className="gap-8">
      {shouldShowTimingIntro && (
        <View className="gap-6 px-1">
          <Body className="text-pretty text-left text-base/normal">
            {t(($) => $.flow.introNoAssetTiming)}
          </Body>
          {realtorId && (
            <RealtorCard userId={realtorId} size="default" showContactInfo className="mx-auto" />
          )}
          <WhatIsAikoaModal />
        </View>
      )}
      <QuestionRenderer
        key={question.id}
        question={question}
        answer={currentAnswer}
        onUpdateAnswer={(value) => {
          if (question.type === 'multi-choice' && typeof value === 'number') {
            onUpdateAnswer(question.id, value)
            if (!(question.id === 'timing' && value === 0)) {
              setTimeout(() => nextPhase(), 300)
            }
          } else if (question.type === 'text-input' && typeof value === 'string') {
            onUpdateAnswer(question.id, value)
          }
        }}
      />
      {currentAnswer === undefined && (
        <View className="gap-4 pb-4">
          <Text className="text-center font-light text-2xl/extra-loose text-neutral-400">
            {t(($) => $.flow.selectAnswer)}
          </Text>
          {currentPhaseIndex > 0 && (
            <Button variant="ghost" size="default" onPress={previousPhase}>
              {t(($) => $.flow.back)}
            </Button>
          )}
        </View>
      )}
    </View>
  )
}

export function QuestionRenderer({
  question,
  answer,
  onUpdateAnswer,
}: {
  question: Question
  answer: string | number | undefined
  onUpdateAnswer: (value: number | string) => void
}) {
  const { t } = useTranslations('feedback')

  switch (question.type) {
    case 'multi-choice':
      return (
        <MultiChoiceQuestion
          question={question}
          answer={answer === undefined ? undefined : numVal(answer, true)}
          onUpdateAnswer={(value) => onUpdateAnswer(value)}
        />
      )
    case 'text-input':
      return (
        <TextInputQuestion
          question={question}
          answer={typeof answer === 'string' ? answer : undefined}
          onUpdateAnswer={(value) => onUpdateAnswer(value)}
        />
      )
    default:
      return <Text>{t(($) => $.errors.unknownQuestionType)}</Text>
  }
}

export function MultiChoiceQuestion({
  question,
  answer,
  onUpdateAnswer,
}: {
  question: Extract<Question, { type: 'multi-choice' }>
  answer: number | undefined
  onUpdateAnswer: (value: number) => void
}) {
  const { options } = question.config

  const handleSelect = (optionValue: number) => {
    onUpdateAnswer(optionValue)
  }

  return (
    <View className="gap-4">
      <View className="gap-2">
        <ViewTitle className="text-pretty border-0">{question.question}</ViewTitle>
        {question.description && <SectionContent>{question.description}</SectionContent>}
      </View>
      <View className="gap-3">
        {options.map((option: (typeof options)[number]) => (
          <ToggleButton
            key={option.value}
            value={answer === option.value}
            onValueChange={() => handleSelect(option.value)}
            testID={`feedback-question-option-${question.id}-${String(option.value).replace('.', '-')}`}>
            {option.label}
          </ToggleButton>
        ))}
      </View>
    </View>
  )
}

export function TextInputQuestion({
  question,
  answer,
  onUpdateAnswer,
}: {
  question: Extract<Question, { type: 'text-input' }>
  answer: string | undefined
  onUpdateAnswer: (value: string) => void
}) {
  return (
    <View className="gap-4">
      <View className="gap-2">
        <ViewTitle className="text-pretty border-0">{question.question}</ViewTitle>
        {question.description && <SectionContent>{question.description}</SectionContent>}
      </View>
      <Input
        value={answer ?? ''}
        onChangeText={onUpdateAnswer}
        placeholder={question.config.placeholder}
        multiline={question.config.multiline ?? false}
        variant="box"
      />
    </View>
  )
}
